import { Button, DialogV2, Loader, Pill, Typography } from '@recruit-robin/robin-theme/web-components';
import { RefreshCw_02, Settings } from '@recruit-robin/robin-theme/web-icons';
import { useEffect, useRef } from 'react';

import { useDictionary } from '@/shared/lib/i18n';
import { useGetNylasConnectionStatusQuery } from '../../api';
import { isCalendarValid } from '../../lib';
import { NylasConnectionStatus } from '../../model/nylas-connection-status.dto';
import { useStyles } from './ManageConnection.styles';
import { useConnectNylasCalendarOpenOAuthPopup } from './use-connect-nylas-calendar';

const getStatusLabel = (status: string, dictionary: ReturnType<typeof useDictionary>): string => {
  switch (status) {
    case NylasConnectionStatus.Valid:
      return dictionary.nylasCalendarStatusValid;
    case NylasConnectionStatus.Invalid:
      return dictionary.nylasCalendarStatusInvalid;
    case NylasConnectionStatus.NotConnected:
      return dictionary.nylasCalendarStatusNotConnected;
    case NylasConnectionStatus.Error:
      return dictionary.nylasCalendarStatusError;
    default:
      return dictionary.nylasCalendarStatusUnknown;
  }
};

const getStatusPillType = (status: string): 'success' | 'error' | 'warning' => {
  switch (status) {
    case NylasConnectionStatus.Valid:
      return 'success';
    case NylasConnectionStatus.Invalid:
    case NylasConnectionStatus.Error:
      return 'error';
    case NylasConnectionStatus.NotConnected:
    default:
      return 'warning';
  }
};

const Header = () => {
  const dictionary = useDictionary();
  const { data: nylasConnectionStatus } = useGetNylasConnectionStatusQuery();

  return (
    <DialogV2.Header>
      <Typography variant={(v) => v.heading[2]} color={(c) => c.neutral.variant[80]}>
        {isCalendarValid(nylasConnectionStatus)
          ? dictionary.nylasCalendarConnectionModalCalendarSetUpTitle
          : dictionary.nylasCalendarConnectionModalTitle}
      </Typography>
    </DialogV2.Header>
  );
};

const Body = () => {
  const dictionary = useDictionary();
  const classes = useStyles();
  const { data: nylasConnectionStatus, refetch, isFetching } = useGetNylasConnectionStatusQuery();
  const currentStatus = nylasConnectionStatus?.calendar.status || 'unknown';

  return (
    <DialogV2.Body customCss={classes.body}>
      <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
        {isCalendarValid(nylasConnectionStatus)
          ? dictionary.nylasCalendarConnectionModalCalendarSetUpText
          : dictionary.nylasCalendarConnectionModalDescription}
      </Typography>

      <div css={classes.currentStatus}>
        <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
          {dictionary.currentStatus}
        </Typography>

        <div css={classes.refreshStatusContainer}>
          <span css={classes.refreshStatus} onClick={() => refetch()}>
            {isFetching ? (
              <Loader size={14} variant="ring-4" color={(c) => c.neutral.variant} />
            ) : (
              <RefreshCw_02 size={14} color={(c) => c.neutral.variant[70]} />
            )}
          </span>
          <Pill
            label={getStatusLabel(currentStatus, dictionary)}
            style="icon-removable"
            type={getStatusPillType(currentStatus)}
            padding="both"
          />
        </div>
      </div>
    </DialogV2.Body>
  );
};

const Footer = ({
  onConfirm,
  onCancel,
  footer,
}: {
  onConfirm?: () => void;
  onCancel?: () => void;
  footer?: React.ReactNode;
}) => {
  const dictionary = useDictionary();
  const { data: nylasConnectionStatus, refetch } = useGetNylasConnectionStatusQuery();

  const classes = useStyles();
  const wasAutomaticallyRefetched = useRef(false);

  const handleSetupNylasConnection = useConnectNylasCalendarOpenOAuthPopup({
    onClosePopup: () => {
      refetch();
      wasAutomaticallyRefetched.current = true;
    },
  });

  useEffect(() => {
    if (wasAutomaticallyRefetched.current) {
      onConfirm?.();

      wasAutomaticallyRefetched.current = false;
    }
  }, [nylasConnectionStatus]);

  return (
    <DialogV2.Footer>
      {footer}

      <div css={classes.footerActions}>
        <Button label={dictionary.maybeLater} style="text" type="subtle1" onClick={onCancel ?? (() => {})} />
        {isCalendarValid(nylasConnectionStatus) ? (
          <Button label={dictionary.continue} style="filled" type="highlight" onClick={onConfirm ?? (() => {})} />
        ) : (
          <Button
            label={dictionary.goToEmailSettings}
            startIcon={Settings}
            style="filled"
            type="highlight"
            onClick={handleSetupNylasConnection}
          />
        )}
      </div>
    </DialogV2.Footer>
  );
};

export const ManageConnection = ({
  onConfirm,
  onCancel,
  footer,
}: {
  onConfirm?: () => void;
  onCancel?: () => void;
  footer?: React.ReactNode;
}) => {
  return (
    <>
      <Header />
      <Body />
      <Footer onConfirm={onConfirm} onCancel={onCancel} footer={footer} />
    </>
  );
};
